import { useState, useEffect, useCallback } from 'react';
import { cookieManager, COOKIE_TYPES, trackAnalyticsEvent, trackPageView } from '../utils/cookieManager';

export const useCookieConsent = () => {
  const [hasConsent, setHasConsent] = useState(cookieManager.hasConsent());
  const [preferences, setPreferences] = useState(cookieManager.getPreferences());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Initialize consent state
    const updateConsentState = () => {
      setHasConsent(cookieManager.hasConsent());
      setPreferences(cookieManager.getPreferences());
      setIsLoading(false);
    };

    // Listen for consent changes
    const handleConsentChange = (event) => {
      setPreferences(event.detail.preferences);
      setHasConsent(cookieManager.hasConsent());
    };

    // Initial state update
    updateConsentState();

    // Add event listener for consent changes
    window.addEventListener('cookieConsentChanged', handleConsentChange);

    // Initialize services if consent already given
    if (cookieManager.hasConsent() && !cookieManager.isConsentExpired()) {
      cookieManager.initializeServices();
    }

    return () => {
      window.removeEventListener('cookieConsentChanged', handleConsentChange);
    };
  }, []);

  // Track analytics events
  const trackEvent = useCallback((eventName, parameters = {}) => {
    if (preferences[COOKIE_TYPES.ANALYTICS]) {
      trackAnalyticsEvent(eventName, parameters);
    }
  }, [preferences]);

  // Track page views
  const trackPage = useCallback((path) => {
    if (preferences[COOKIE_TYPES.ANALYTICS]) {
      trackPageView(path);
    }
  }, [preferences]);

  // Set consent preferences
  const setConsent = useCallback((newPreferences) => {
    cookieManager.setConsent(newPreferences);
  }, []);

  // Update specific consent type
  const updateConsentType = useCallback((type, consent) => {
    cookieManager.updateConsent(type, consent);
  }, []);

  // Revoke all consent
  const revokeConsent = useCallback(() => {
    cookieManager.revokeConsent();
  }, []);

  // Check if specific cookie type is allowed
  const hasConsentFor = useCallback((type) => {
    return preferences[type] || false;
  }, [preferences]);

  // Get consent date
  const getConsentDate = useCallback(() => {
    return cookieManager.getConsentDate();
  }, []);

  // Check if consent is expired
  const isConsentExpired = useCallback(() => {
    return cookieManager.isConsentExpired();
  }, []);

  return {
    // State
    hasConsent,
    preferences,
    isLoading,
    
    // Actions
    setConsent,
    updateConsentType,
    revokeConsent,
    
    // Utilities
    hasConsentFor,
    getConsentDate,
    isConsentExpired,
    
    // Tracking
    trackEvent,
    trackPage,
    
    // Cookie types for easy access
    COOKIE_TYPES
  };
};

export default useCookieConsent; 